/**
 * useFlexScroll — the "flexible mesh" feel for any horizontal scroll track:
 * cards lean and skew with scroll velocity through a spring and settle
 * back; desktop drag-to-scroll with inertia and snap-back; a progress
 * indicator; links stay clickable (a drag never counts as a click).
 * Native touch scrolling is untouched — the physics only read velocity.
 */
import { useEffect, useState, type RefObject } from "react";

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v));

export interface FlexScrollOptions {
  /** Selector (inside the track) for the elements that lean. */
  cardSelector: string;
  /** Selector (inside each card) for the element that gets the parallax nudge. */
  mediaSelector?: string;
  /** Element whose width/left show scroll progress. */
  progressRef?: RefObject<HTMLElement | null>;
  /** Lean strength (deg at full velocity). */
  rotate?: number;
  /** Parallax nudge in px at full velocity. */
  parallax?: number;
  /** Baseline scale kept on the media so the nudge never exposes edges. */
  mediaScale?: number;
}

export function useFlexScroll(trackRef: RefObject<HTMLElement | null>, opts: FlexScrollOptions): { dragging: boolean } {
  const [dragging, setDragging] = useState(false);
  const { cardSelector, mediaSelector, progressRef, rotate = 16, parallax = 14, mediaScale = 1.08 } = opts;

  useEffect(() => {
    const track = trackRef.current;
    if (!track) return;
    const reduce = window.matchMedia?.("(prefers-reduced-motion: reduce)").matches;
    const cards = Array.from(track.querySelectorAll<HTMLElement>(cardSelector));
    const medias = cards.map((c) => (mediaSelector ? c.querySelector<HTMLElement>(mediaSelector) : null));
    const baseMedia = `scale(${mediaScale})`;
    medias.forEach((m) => { if (m) m.style.transform = baseMedia; });

    let lastX = track.scrollLeft, lastT = performance.now();
    let vel = 0, lean = 0, leanV = 0, raf = 0, running = false;

    const updateProgress = () => {
      const p = progressRef?.current;
      if (!p) return;
      const max = track.scrollWidth - track.clientWidth;
      const frac = max > 0 ? track.scrollLeft / max : 0;
      const w = clamp(track.clientWidth / track.scrollWidth, 0.12, 1);
      p.style.width = `${w * 100}%`;
      p.style.left = `${frac * (100 - w * 100)}%`;
    };

    const frame = (now: number) => {
      const dt = Math.min(48, now - lastT) || 16;
      vel *= Math.pow(0.86, dt / 16);
      const target = clamp(vel * 0.9, -1, 1);
      const k = 0.16, d = 0.72;
      leanV = (leanV + (target - lean) * k) * d;
      lean += leanV;
      const centre = track.scrollLeft + track.clientWidth / 2;
      cards.forEach((card, i) => {
        const cc = card.offsetLeft + card.offsetWidth / 2;
        const off = clamp((cc - centre) / track.clientWidth, -1, 1);
        card.style.transform = `perspective(1200px) rotateY(${(-lean * rotate - off * 4).toFixed(2)}deg) skewY(${(-lean * 3.5).toFixed(2)}deg)`;
        const m = medias[i];
        if (m) m.style.transform = `translateX(${(-lean * parallax - off * (parallax * 0.55)).toFixed(1)}px) ${baseMedia}`;
      });
      lastT = now;
      if (Math.abs(lean) > 0.002 || Math.abs(leanV) > 0.002 || Math.abs(vel) > 0.01) raf = requestAnimationFrame(frame);
      else {
        cards.forEach((c, i) => { c.style.transform = ""; const m = medias[i]; if (m) m.style.transform = baseMedia; });
        running = false;
      }
    };
    const kick = () => { if (running) return; running = true; lastT = performance.now(); raf = requestAnimationFrame(frame); };
    const onScroll = () => {
      const now = performance.now();
      const dt = Math.max(1, now - lastT);
      const dx = track.scrollLeft - lastX;
      lastX = track.scrollLeft;
      if (!reduce) { vel = vel * 0.6 + (dx / dt) * 0.4; kick(); }
      updateProgress();
    };
    track.addEventListener("scroll", onScroll, { passive: true });
    updateProgress();
    const ro = new ResizeObserver(updateProgress);
    ro.observe(track);

    // Desktop drag with inertia; a drag suppresses the click that ends it.
    let down = false, startX = 0, startLeft = 0, moved = 0, px = 0, pt = 0, pv = 0, inertia = 0, dragged = false;
    const snapTo = (behavior: ScrollBehavior) => {
      const centre = track.scrollLeft + track.clientWidth / 2;
      let best: HTMLElement | null = null, bestD = Infinity;
      for (const c of cards) { const d = Math.abs(c.offsetLeft + c.offsetWidth / 2 - centre); if (d < bestD) { bestD = d; best = c; } }
      if (best) track.scrollTo({ left: best.offsetLeft + best.offsetWidth / 2 - track.clientWidth / 2, behavior });
    };
    const glide = () => {
      if (Math.abs(pv) < 0.02) { track.classList.remove("is-free"); snapTo("smooth"); return; }
      track.scrollLeft -= pv * 16; pv *= 0.94;
      inertia = requestAnimationFrame(glide);
    };
    // A press is NOT a drag. Nothing changes on pointerdown except the
    // bookkeeping: `is-dragging` (which switches the cards' pointer-events
    // off) and `is-free` only go on once the pointer has actually travelled
    // 6px. Before 2026-09-08 they went on at pointerdown, so on a plain
    // desktop click the card under the cursor lost pointer-events between
    // press and release, the click was dispatched to the track instead of
    // the link, and no product card on the homepage navigated (Hayden:
    // "when i click on any product card the click doesn't work"). Touch
    // never took this path — native scrolling handles it — which is why
    // phones were fine.
    const onDown = (e: PointerEvent) => {
      if (e.pointerType !== "mouse" || e.button !== 0) return;
      cancelAnimationFrame(inertia);
      down = true; moved = 0; startX = e.clientX; startLeft = track.scrollLeft; px = e.clientX; pt = performance.now(); pv = 0;
    };
    const onMove = (e: PointerEvent) => {
      if (!down) return;
      const dx = e.clientX - startX;
      moved = Math.max(moved, Math.abs(dx));
      if (!dragged) {
        if (moved <= 6) return; // still a click
        dragged = true;
        track.classList.add("is-free");
        setDragging(true);
      }
      track.scrollLeft = startLeft - dx;
      const now = performance.now();
      pv = pv * 0.5 + ((e.clientX - px) / Math.max(1, now - pt)) * 0.5;
      px = e.clientX; pt = now;
    };
    const onUp = () => {
      if (!down) return;
      down = false;
      if (!dragged) return; // a click: nothing was changed, nothing to undo
      setDragging(false);
      inertia = requestAnimationFrame(glide);
      // Keep suppressing the click that ends a drag; it fires right after pointerup.
      setTimeout(() => { dragged = false; }, 60);
    };
    const onClickCapture = (e: MouseEvent) => { if (dragged) { e.preventDefault(); e.stopPropagation(); } };
    track.addEventListener("pointerdown", onDown);
    window.addEventListener("pointermove", onMove);
    window.addEventListener("pointerup", onUp);
    window.addEventListener("pointercancel", onUp);
    track.addEventListener("click", onClickCapture, true);
    return () => {
      cancelAnimationFrame(raf); cancelAnimationFrame(inertia);
      track.removeEventListener("scroll", onScroll);
      track.removeEventListener("pointerdown", onDown);
      window.removeEventListener("pointermove", onMove);
      window.removeEventListener("pointerup", onUp);
      window.removeEventListener("pointercancel", onUp);
      track.removeEventListener("click", onClickCapture, true);
      ro.disconnect();
      cards.forEach((c, i) => { c.style.transform = ""; const m = medias[i]; if (m) m.style.transform = ""; });
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trackRef, cardSelector, mediaSelector, rotate, parallax, mediaScale]);

  return { dragging };
}
